import { Building2, User } from 'lucide-react'
import type { ListPageColumn } from '@/components/templates/list-page'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { displayDateTime } from '@/lib/dates'
import { QueueActions, VendorLink } from './QueueActions'
import { reasonLabels, type UnmatchedItem } from './types'
import type { useQueueSelection, useUnmatchedItems } from './useUnmatchedItems'

type Options = {
  sortable: ReturnType<typeof useUnmatchedItems>['sortable']
  selection: ReturnType<typeof useQueueSelection>
  viewOnly: boolean
  pending: boolean
  onLink: (row: UnmatchedItem) => void
  onAction: (row: UnmatchedItem, action: string) => void
  onIgnore: (row: UnmatchedItem) => void
}
export function queueColumns(options: Options): ListPageColumn<UnmatchedItem>[] {
  const columns: ListPageColumn<UnmatchedItem>[] = [
    {
      id: 'party',
      header: 'Party',
      sort: options.sortable('name'),
      className: 'px-2',
      cell: (row) => <Party row={row} />,
    },
    {
      id: 'connection',
      header: 'Connection',
      headerClassName: 'w-28 px-2',
      className: 'px-2',
      cell: (row) => <span className="text-muted-foreground">{row.connection_name}</span>,
    },
    {
      id: 'reason',
      header: 'Reason',
      headerClassName: 'w-28 px-2',
      className: 'px-2',
      cell: (row) => (
        <div className="min-w-0 leading-snug">
          <Badge variant="neutral" className="whitespace-normal">
            {reasonLabels[row.reason]}
          </Badge>
          {row.reason_detail && (
            <div className="truncate text-caption text-muted-foreground" title={row.reason_detail}>
              {row.reason_detail}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'waiting',
      header: 'Waiting',
      sort: options.sortable('waiting_rows'),
      headerClassName: 'w-[5.5rem] px-2',
      className: 'px-2 font-medium',
      cell: (row) => `${row.waiting_rows} ${row.waiting_rows === 1 ? 'row' : 'rows'} waiting`,
    },
    {
      id: 'first-seen',
      header: 'First seen',
      headerClassName: 'w-[5.25rem] px-2',
      className: 'px-2 text-caption text-muted-foreground',
      cell: (row) => <SeenTime value={row.first_seen_at} />,
    },
    {
      id: 'last-seen',
      header: 'Last seen',
      sort: options.sortable('last_seen'),
      headerClassName: 'w-[5.25rem] px-2',
      className: 'px-2 text-caption text-muted-foreground',
      cell: (row) => <SeenTime value={row.last_seen_at} />,
    },
  ]
  if (!options.viewOnly) {
    columns.unshift(selectionColumn(options.selection))
    columns.push({
      id: 'actions',
      header: '',
      headerClassName: 'w-64 px-2',
      className: 'whitespace-nowrap px-2',
      cell: (row) => <QueueActions row={row} {...options} />,
    })
  }
  return columns
}
function SeenTime({ value }: { value: string }) {
  const parts = displayDateTime(value).split(', ')
  return (
    <time dateTime={value} title={displayDateTime(value)}>
      <span className="block">{parts.slice(0, -1).join(', ')}</span>
      <span className="block">{parts.at(-1)}</span>
    </time>
  )
}
function selectionColumn(selection: Options['selection']): ListPageColumn<UnmatchedItem> {
  return {
    id: 'select',
    header: (
      <Checkbox
        aria-label="Select all unmatched items"
        checked={selection.all ? true : selection.ids.length > 0 ? 'indeterminate' : false}
        onCheckedChange={selection.toggleAll}
      />
    ),
    headerClassName: 'w-8 px-2',
    className: 'px-2',
    cell: (row) =>
      row.status === 'open' && (
        <Checkbox
          aria-label={`Select ${row.name_snapshot}`}
          checked={selection.ids.includes(row.id)}
          onCheckedChange={() => selection.toggle(row.id)}
        />
      ),
  }
}
function Party({ row }: { row: UnmatchedItem }) {
  const Icon = row.party_kind === 'person' ? User : Building2
  const caption = [row.email_snapshot, row.organization_snapshot].filter(Boolean).join(' · ')
  return (
    <div className="flex items-start gap-2">
      <Icon
        className="mt-0.5 size-4 shrink-0 text-muted-foreground"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <div className="min-w-0 leading-snug">
        <div className="flex min-w-0 items-center gap-2 font-medium">
          <span className="min-w-0 truncate" title={row.name_snapshot}>
            {row.name_snapshot}
          </span>
          <VendorLink row={row} />
        </div>
        {caption && (
          <div className="truncate text-caption text-muted-foreground" title={caption}>
            {caption}
          </div>
        )}
      </div>
    </div>
  )
}
